<script setup lang="tsx">
import type { DocTreeNode, Response, Document, EditFolderRequest, URL } from '@/models/api-generated'
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import http from '@/services/http'
import DocumentFiles from './DocumentFiles.vue'
import DocumentHistory from './DocumentHistory.vue'
import IconButton from '../shared/IconButton.vue'
import FolderNode from './FolderNode.vue'
import { hasPermission, viewing } from '@/services/utils'
import { PermDocumentAdd, PermDocumentRead } from '@/models/enums-generated'
import { folderRefreshCallback, selectedFolder } from './shared'
import LoadingContainer from '../widgets/LoadingContainer.vue'

const props = defineProps<{
	showSelect?: boolean
	showHistory?: boolean
	preselectDocument?: Document
	preselectUrl?: URL
	selectionType?: 'all' | 'document' | 'url'
}>()
const emit = defineEmits<{
	(e: 'file-selected', file: Document): void
	(e: 'url-selected', url: URL): void
}>()

const isLoading = ref(false)
const root = ref<DocTreeNode & { isOpen?: boolean }>()
const newFolderName = ref('')
const showInput = ref(false)
const newFolderInput = ref<HTMLInputElement>()
const canView = hasPermission(PermDocumentRead)
const canAdd = hasPermission(PermDocumentAdd) && viewing.value > 0
const canRemove = computed(() => {
	if (!selectedFolder.value) return false
	if (root.value && selectedFolder.value === root.value) return false
	return !selectedFolder.value.children?.length && !selectedFolder.value.documents?.length && canAdd
})
const selectionType = computed(() => props.selectionType ?? 'all')

folderRefreshCallback.value = fetchDocuments

function handleFileSelect(file: Document) {
	emit('file-selected', file)
}
function handleUrlSelect(url: URL) {
	emit('url-selected', url)
}

async function fetchDocuments(pathToOpen?: string) {
	if (!canView) return
	isLoading.value = true
	const { data } = await http.get<Response<DocTreeNode>>(`/api/document/tree`)
	if (!data.data) return
	root.value = { ...data.data, isOpen: true }

	if (pathToOpen) openFolderForDocument(root.value, pathToOpen)
	else selectedFolder.value = root.value
	isLoading.value = false
}

function traverse(node: DocTreeNode, normalizedDocPath: string, pathToNode: DocTreeNode[]): boolean {
	const normalizedNodePath = node.path.replace(/\/+/g, '/').replace(/\/$/, '')
	if (normalizedNodePath === normalizedDocPath) {
		pathToNode.push(node)
		return true
	}
	for (const child of node.children ?? []) {
		if (traverse(child, normalizedDocPath, pathToNode)) {
			pathToNode.unshift(node)
			return true
		}
	}
	return false
}

function closeAll(node: DocTreeNode) {
	node.isOpen = false
	node.children?.forEach(closeAll)
}

function openFolderForDocument(rootNode: DocTreeNode, docPath: string) {
	const normalizedDocPath = docPath.replace(/\/+/g, '/').replace(/\/$/, '')
	const pathToNode: DocTreeNode[] = []

	if (!traverse(rootNode, normalizedDocPath, pathToNode)) return

	closeAll(rootNode)
	pathToNode.forEach((node) => (node.isOpen = true))
	selectedFolder.value = pathToNode[pathToNode.length - 1]
}

function addFolder() {
	if (!selectedFolder.value) return
	showInput.value = !showInput.value
	if (showInput.value) nextTick(() => newFolderInput.value?.focus())
	else newFolderName.value = ''
}

async function confirmAdd() {
	if (!selectedFolder.value || !newFolderName.value.trim()) return
	if (selectedFolder.value.children.find((x) => x.name === newFolderName.value)) return

	const node: DocTreeNode = {
		name: newFolderName.value,
		path: `${selectedFolder.value.path}${newFolderName.value}/`,
		children: [],
		documents: [],
		urls: [],
		isOpen: false,
	}

	const { status } = await http.post('api/document/tree', { path: node.path })
	if (status === 200) {
		selectedFolder.value.children = [...(selectedFolder.value.children ?? []), node]
		newFolderName.value = ''
		showInput.value = false
	}
}

async function removeFolder() {
	if (!root.value || !selectedFolder.value || root.value === selectedFolder.value) return
	if (selectedFolder.value.children?.length || selectedFolder.value.documents?.length) return

	await http.delete('api/document/tree', { data: { path: selectedFolder.value.path } })

	function filterChildren(node: DocTreeNode): DocTreeNode {
		return {
			...node,
			children: node.children?.filter((child) => child !== selectedFolder.value).map(filterChildren) || [],
		}
	}

	root.value = filterChildren(root.value)
	selectedFolder.value = undefined
}

async function handleRename(node: DocTreeNode, path: string, name: string) {
	const request: EditFolderRequest = { oldPath: node.path, newPath: path }
	const { status } = await http.put('/api/document/tree', request)
	if (status === 200) {
		node.name = name
		node.path = path
		selectedFolder.value = node
	}
}

watch(
	() => props.preselectDocument,
	(doc) => {
		if (!doc || !root.value) return
		openFolderForDocument(root.value, doc.path)
	},
	{ immediate: true },
)
watch(
	() => props.preselectUrl,
	(url) => {
		if (!url || !root.value) return
		fetchDocuments(url.path)
	},
	{ immediate: true },
)

onMounted(fetchDocuments)
</script>

<template>
	<div class="row">
		<div class="col-12 col-xl-4 d-flex">
			<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3 text-nowrap flex-fill overflow-hidden">
				<div class="d-flex flex-column flex-fill overflow-hidden">
					<div class="flex-grow-1 overflow-auto w-100">
						<FolderNode
							v-if="root"
							v-model="root"
							@select-folder="selectedFolder = $event"
							@folder-renamed="handleRename"
							class="root w-100"
						/>
					</div>
					<div class="d-flex flex-column align-items-center w-100 mt-2">
						<div class="d-flex justify-content-center gap-4">
							<IconButton
								v-if="canAdd"
								id="add-document"
								class="bi bi-folder-plus"
								@click="addFolder"
								:title="$t('misc.add')"
								size="fs-4"
							/>
							<div v-if="viewing" v-one-tooltip="!canRemove ? $t('document.cantRemove') : ''">
								<IconButton
									id="remove-document"
									class="bi bi-folder-x"
									@click="removeFolder"
									:title="$t('misc.remove')"
									size="fs-4"
									:disabled="!canRemove"
								/>
							</div>
						</div>
						<div v-if="showInput" class="mt-2 d-flex">
							<input
								v-model="newFolderName"
								type="text"
								class="form-control me-2"
								:placeholder="$t('document.folderName')"
								@keydown.enter="confirmAdd"
								@keydown.escape="addFolder"
								ref="newFolderInput"
							/>
							<IconButton id="confirm-add-document" class="bi bi-plus-circle" @click="confirmAdd" :title="$t('misc.add')" size="fs-4" />
						</div>
					</div>
				</div>
			</LoadingContainer>
		</div>
		<div class="col-12 col-xl-8 d-flex">
			<DocumentFiles
				v-if="selectedFolder"
				v-model="selectedFolder"
				@select-file="handleFileSelect"
				@select-url="handleUrlSelect"
				:show-select="props.showSelect"
				:filter-type="selectionType"
				:show-history="showHistory"
			/>
		</div>
		<!-- <div v-if="!props.showSelect" class="col-12 col-xl-3 d-flex">
			<DocumentHistory />
		</div> -->
	</div>
</template>
